import { useEffect } from 'react';

/**
 * XindingsTooltips — universal hover-tooltips on every .xindings element.
 *
 * The XINDINGS font renders every Latin character as an inscrutable
 * gorgeous glyph. First-time visitors have no way to know what those
 * shapes actually *say* — the whole feature dies unless we surface the
 * plaintext behind the glyphs on hover.
 *
 * Why we don't use the native `title=""` attribute alone:
 *   1. Browsers delay title-tooltips by 1–2 seconds — feels broken.
 *   2. Native tooltips are invisible on touch devices (mobile = 60% of us).
 *   3. Native tooltips render in an OS-grey box, breaking the Xin aesthetic.
 *
 * How this works:
 *   • On every mutation to the DOM, we walk `.xindings` elements and copy
 *     their plaintext into three attributes:
 *       - `title`        — accessibility & native-fallback for screen readers
 *       - `aria-label`   — assistive-tech pronunciation
 *       - `data-xin-hint` — used by our CSS `::after` pseudo-element
 *   • A single injected <style> block renders the pseudo-element as a
 *     cinematic aubergine + gold pill that appears instantly on `:hover`
 *     (and on `:focus-visible` for keyboard users).
 *
 * Zero JS on hover — pure CSS reveals — so it's fast, cheap, and
 * flicker-free even on low-end phones.
 */

// Wrapped in one string so it's a single style-tag we can hot-toggle.
const TOOLTIP_CSS = `
.xindings {
  position: relative;
  cursor: help;
}
/* The floating hint bubble. Positioned above the glyph run, centred. */
.xindings[data-xin-hint]:hover::after,
.xindings[data-xin-hint]:focus-visible::after {
  content: attr(data-xin-hint);
  position: absolute;
  bottom: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  z-index: 9999;
  padding: 8px 14px;
  border-radius: 999px;
  /* Force a normal reading font — otherwise CSS inheritance would render
     the tooltip in XINDINGS glyphs too, which is the whole problem. */
  font-family: 'Instrument Sans', 'Inter', system-ui, sans-serif;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.3px;
  line-height: 1.2;
  color: #E8B84B;
  background: rgba(20, 15, 26, 0.96);
  border: 1px solid rgba(232, 184, 75, 0.55);
  box-shadow: 0 12px 30px rgba(0,0,0,0.45);
  white-space: nowrap;
  max-width: min(80vw, 520px);
  overflow: hidden;
  text-overflow: ellipsis;
  pointer-events: none;
  animation: xindings-hint-in 140ms ease-out both;
}
/* Little gold arrow under the bubble. Uses ::before so it's independent of
   the ::after content-string. */
.xindings[data-xin-hint]:hover::before,
.xindings[data-xin-hint]:focus-visible::before {
  content: '';
  position: absolute;
  bottom: calc(100% + 2px);
  left: 50%;
  transform: translateX(-50%);
  z-index: 9998;
  border: 6px solid transparent;
  border-top-color: rgba(232, 184, 75, 0.55);
  pointer-events: none;
  animation: xindings-hint-in 140ms ease-out both;
}
@keyframes xindings-hint-in {
  from { opacity: 0; transform: translateX(-50%) translateY(4px); }
  to   { opacity: 1; transform: translateX(-50%) translateY(0);   }
}
/* Long strings wrap gracefully on smaller viewports. */
@media (max-width: 640px) {
  .xindings[data-xin-hint]:hover::after {
    white-space: normal;
    max-width: min(80vw, 320px);
    text-align: center;
  }
}
`;

export default function XindingsTooltips() {
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;

    // Inject the CSS once. Idempotent — HMR / StrictMode re-runs are safe.
    const STYLE_ID = 'xindings-tooltip-styles';
    if (!document.getElementById(STYLE_ID)) {
      const style = document.createElement('style');
      style.id = STYLE_ID;
      style.textContent = TOOLTIP_CSS;
      document.head.appendChild(style);
    }

    // Copy plaintext into title / aria-label / data-xin-hint for every
    // .xindings element. We diff before writing to avoid DOM churn on
    // re-renders (the MutationObserver would otherwise stack-overflow).
    const sync = (root) => {
      const scope = (root && root.querySelectorAll) ? root : document.body;
      const nodes = scope.querySelectorAll ? scope.querySelectorAll('.xindings') : [];
      nodes.forEach((el) => {
        const text = (el.textContent || '').trim();
        if (!text) return;
        if (el.getAttribute('data-xin-hint') !== text) el.setAttribute('data-xin-hint', text);
        if (el.getAttribute('title') !== text) el.setAttribute('title', text);
        // aria-hidden elements must not be focusable (WAI-ARIA rule) and shouldn't
        // duplicate aria-label — skip both for these decorative glyphs.
        const ariaHidden = el.getAttribute('aria-hidden') === 'true';
        if (!ariaHidden) {
          if (el.getAttribute('aria-label') !== text) el.setAttribute('aria-label', text);
          // Make keyboard users first-class citizens on the interactive glyphs.
          if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '0');
        } else {
          // Clean up any stale tabindex on decorative glyphs.
          if (el.getAttribute('tabindex') === '0') el.removeAttribute('tabindex');
        }
      });
    };

    // Initial pass — the DOM already has plenty of .xindings by mount time.
    sync(document.body);

    // Watch for new .xindings elements from route changes, lazy loads,
    // modal opens, etc.
    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        m.addedNodes.forEach((n) => {
          if (n.nodeType !== 1) return;
          if (n.classList?.contains('xindings')) sync(n.parentNode);
          else sync(n);
        });
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, []);

  return null;
}
